// URL param seam for the Unified Feedback Inbox — kind, status, rating,
// location_ids, q(uery), page, per_page, item, lookback. See
// veda:projects/unified-feedback-inbox/docs/11-chassis-plan.md §2 PR 01 and
// docs/06-ux-rules.md "Any filter change" / "Look-back, R3".
//
// react-router-dom is pinned to ^4.1.1 in this repo, which predates the
// `useHistory`/`useLocation` hooks (added in v5.1) — so this hook can't use
// them. The established pattern elsewhere in this codebase (e.g.
// `scenes/UserLists/Detail/index.tsx`) is to import the app's single
// `history` singleton directly from `store` rather than reading it via
// `withRouter`/route-match props, which would force every consumer of this
// hook to be a route match. This hook follows that precedent. Reactivity
// (re-rendering when the URL changes — including browser Back/Forward) comes
// from `history.listen`, not from React Router context.
import type {
  InboxKind,
  InboxStatus,
  RatingFilter,
  SetupState,
} from 'api/feedbackInbox.types'
import type { PageSize } from 'components/TablePaginationFooter'
import { PAGE_SIZE_OPTIONS } from 'components/TablePaginationFooter'
import type { History } from 'history'
import xor from 'lodash/xor'
import { useCallback, useEffect, useRef, useState } from 'react'
import { history as defaultHistory } from 'store'
import { DEFAULT_PAGE_SIZE } from './constants'

const INBOX_KINDS: readonly InboxKind[] = ['feedback', 'recovery_ai', 'all']
const INBOX_STATUSES: readonly InboxStatus[] = ['open', 'resolved', 'all']
const RATING_FILTERS: readonly RatingFilter[] = ['up', 'down']

function isInboxKind(value: string | null): value is InboxKind {
  return value !== null && (INBOX_KINDS as readonly string[]).includes(value)
}

function isInboxStatus(value: string | null): value is InboxStatus {
  return value !== null && (INBOX_STATUSES as readonly string[]).includes(value)
}

function isRatingFilter(value: string | null): value is RatingFilter {
  return value !== null && (RATING_FILTERS as readonly string[]).includes(value)
}

// `per_page` used to reach the API as a raw, unvalidated `number` (parsed
// here) while only the FOOTER's displayed `pageSize` was coerced to a valid
// `PageSize` (via the now-removed `toPageSize.ts`) — so `?per_page=33` sent
// `per_page=33` to the API while the footer showed `25`. Validating here,
// once, at the single source of truth every consumer reads, means
// `InboxParams.perPage` is always a valid `PageSize` everywhere it's used.
function isPageSize(value: number): value is PageSize {
  return (PAGE_SIZE_OPTIONS as readonly number[]).includes(value)
}

export interface InboxParams {
  kind: InboxKind
  status: InboxStatus
  rating: RatingFilter | null
  locationIds: number[]
  q: string
  page: number
  perPage: PageSize
  item: string | null
  lookback: string | null
}

// Mount-supplied defaults (docs/06-ux-rules.md "Mount-supplied defaults") —
// applied only when the URL itself doesn't already specify a value.
export interface InboxInitialParams {
  kind?: InboxKind
  status?: InboxStatus
}

export type InboxFilterPatch = Partial<
  Pick<InboxParams, 'kind' | 'status' | 'rating' | 'locationIds'>
>

export interface SetSearchOptions {
  // The ids currently visible in the list. When the selected `item` is not
  // among them after the search change, it's cleared — kept otherwise.
  visibleItemIds?: string[]
}

// Setup-state fallback (docs/06-ux-rules.md "URL kind=feedback fallback" +
// "Setup-state gating"): RAI off ⇒ recovery_ai falls back to feedback; RAI
// fully rolled out (`all`) ⇒ feedback falls back to recovery_ai (the Order
// Feedback segment/tab doesn't exist in that state). Exported for its unit
// test.
//
// Written comments off (FR-23): the Order Feedback segment lists only
// commented thumbs-downs, so it is always empty and is hidden. A kind=feedback
// request resolves to `all`, which lists every rating and RAI escalation.
export function resolveKind(
  kind: InboxKind,
  setupState: SetupState,
  writtenFeedbackEnabled = true
): InboxKind {
  let resolved = kind
  if (resolved === 'recovery_ai' && setupState === 'off') resolved = 'feedback'
  if (resolved === 'feedback' && setupState === 'all') return 'recovery_ai'
  if (resolved === 'feedback' && !writtenFeedbackEnabled) return 'all'
  return resolved
}

// Applies a filter bar patch the way `setFilter` does: back to page 1, then
// the setup-state kind fallback and the rating-clearing rule.
function applyFilterPatch(
  current: InboxParams,
  patch: InboxFilterPatch,
  setupState: SetupState,
  writtenFeedbackEnabled = true
): InboxParams {
  const next: InboxParams = { ...current, ...patch, page: 1 }
  next.kind = resolveKind(next.kind, setupState, writtenFeedbackEnabled)
  if (next.kind !== 'all') next.rating = null
  return next
}

// True when `patch` leaves every filter as it is, e.g. a click on the Status
// or Kind segment that is already active. Location order does not matter.
// Exported so the container can skip the discard confirm for it.
export function isNoopFilterPatch(
  current: InboxParams,
  patch: InboxFilterPatch,
  setupState: SetupState,
  writtenFeedbackEnabled = true
): boolean {
  const next = applyFilterPatch(
    current,
    patch,
    setupState,
    writtenFeedbackEnabled
  )
  return (
    next.kind === current.kind &&
    next.status === current.status &&
    next.rating === current.rating &&
    xor(next.locationIds, current.locationIds).length === 0
  )
}

// Parses `history.location.search` into InboxParams, applying
// `initialParams` fallbacks and the setup-state kind fallback + rating-
// clearing rule. Exported for its unit test — the hook below just wires this
// up to `history.listen`.
export function parseInboxParams(
  search: string,
  initialParams: InboxInitialParams,
  setupState: SetupState,
  writtenFeedbackEnabled = true
): InboxParams {
  const url = new URLSearchParams(search)

  const rawKind = url.get('kind')
  const kind = resolveKind(
    isInboxKind(rawKind) ? rawKind : initialParams.kind ?? 'all',
    setupState,
    writtenFeedbackEnabled
  )

  const rawStatus = url.get('status')
  const status = isInboxStatus(rawStatus)
    ? rawStatus
    : initialParams.status ?? 'open'

  // 👍/👎 toggle only exists when Kind = "All" — clear it otherwise
  // (docs/06-ux-rules.md "👍/👎 toggle").
  const rawRating = url.get('rating')
  const rating = kind === 'all' && isRatingFilter(rawRating) ? rawRating : null

  const locationIds = url
    .getAll('location_ids[]')
    .map(id => Number(id))
    .filter(id => Number.isInteger(id))

  const rawPage = Number(url.get('page'))
  const page = Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 1

  const rawPerPage = Number(url.get('per_page'))
  const perPage = isPageSize(rawPerPage) ? rawPerPage : DEFAULT_PAGE_SIZE

  return {
    kind,
    status,
    rating,
    locationIds,
    q: url.get('q') ?? '',
    page,
    perPage,
    item: url.get('item'),
    lookback: url.get('lookback'),
  }
}

// Exported for its unit test.
export function serializeInboxParams(params: InboxParams): URLSearchParams {
  const url = new URLSearchParams()
  url.set('kind', params.kind)
  url.set('status', params.status)
  if (params.rating) url.set('rating', params.rating)
  params.locationIds.forEach(id => url.append('location_ids[]', String(id)))
  if (params.q) url.set('q', params.q)
  url.set('page', String(params.page))
  url.set('per_page', String(params.perPage))
  if (params.item) url.set('item', params.item)
  if (params.lookback) url.set('lookback', params.lookback)
  return url
}

export interface UseInboxParamsResult {
  params: InboxParams
  setFilter: (patch: InboxFilterPatch) => void
  setPage: (page: number, perPage?: PageSize) => void
  setSearch: (q: string, options?: SetSearchOptions) => void
  selectItem: (id: string | null) => void
  openLookback: (id: string) => void
  closeLookback: () => void
}

// `historyOverride` defaults to the app's real singleton (see the module
// comment above) — it exists purely as a test/story seam. Storybook's
// `AppDecorator` wraps every story in its own `createTestStore()`, but that
// store's `routerMiddleware(history)` wiring is separate from this hook's
// `history` import, so it does NOT protect Storybook's iframe URL
// (`?id=&viewMode=story`) from a `history.replace`/`push` call here writing
// over it. None of PR 02's stories call a setter, so the risk doesn't
// materialize yet, but any story with a play function that does (e.g. a
// future resolve-animation story) should pass `createMemoryHistory()` here
// rather than let it hit the real browser history.
export function useInboxParams(
  initialParams: InboxInitialParams,
  setupState: SetupState,
  historyOverride: History = defaultHistory,
  writtenFeedbackEnabled = true
): UseInboxParamsResult {
  const history = historyOverride
  const [params, setParams] = useState<InboxParams>(() =>
    parseInboxParams(
      history.location.search,
      initialParams,
      setupState,
      writtenFeedbackEnabled
    )
  )

  // The documented usage passes `initialParams` inline (e.g.
  // `<FeedbackInbox initialParams={{ kind: 'feedback', status: 'open' }} />`),
  // which is a new object reference every render. `initialKey` collapses it
  // to a stable primitive so the effect below doesn't re-subscribe on every
  // render; `initialParamsRef` lets the listener always read the latest
  // values without needing `initialParams` itself in the deps array.
  const initialParamsRef = useRef(initialParams)
  initialParamsRef.current = initialParams
  const initialKey = `${initialParams.kind ?? ''}|${initialParams.status ?? ''}`

  // Re-render on every navigation — including browser Back/Forward, and any
  // other consumer's push/replace to this same URL — since nothing here
  // subscribes via React Router context. `history.listen` is the single
  // source of truth; every setter below only ever calls `history.replace`/
  // `history.push` and relies on this listener to update `params`, rather
  // than calling `setParams` itself, per the repo's "never nest state
  // updates inside state updater functions" rule (.claude/docs/conventions.md).
  useEffect(
    () =>
      history.listen(location => {
        setParams(
          parseInboxParams(
            location.search,
            initialParamsRef.current,
            setupState,
            writtenFeedbackEnabled
          )
        )
      }),
    [initialKey, setupState, writtenFeedbackEnabled, history]
  )

  const currentParams = useCallback(
    () =>
      parseInboxParams(
        history.location.search,
        initialParams,
        setupState,
        writtenFeedbackEnabled
      ),
    [history, initialParams, setupState, writtenFeedbackEnabled]
  )

  // Filters/search/page use `replace` (docs/06-ux-rules.md URL row); `item`/
  // `lookback` use `push` so browser Back can pop them. `pathname` and `hash`
  // must be passed explicitly — history v4's `createLocation` defaults an
  // omitted `pathname` to `''` (which would navigate the app away from
  // whatever route mounted the inbox rather than just updating its query
  // string) and an omitted `hash` to `''` (which would wipe it). PR 08 mounts
  // this hook inside `OrderFeedbackReport`, whose tabs are hash-driven
  // (`#overview`/`#details`), so the hash must survive every setter here.
  const replaceParams = useCallback(
    (next: InboxParams) => {
      history.replace({
        pathname: history.location.pathname,
        search: `?${serializeInboxParams(next).toString()}`,
        hash: history.location.hash,
      })
    },
    [history]
  )

  const pushParams = useCallback(
    (next: InboxParams) => {
      history.push({
        pathname: history.location.pathname,
        search: `?${serializeInboxParams(next).toString()}`,
        hash: history.location.hash,
      })
    },
    [history]
  )

  const applyKeepItemRule = useCallback(
    (next: InboxParams, options?: SetSearchOptions): InboxParams => {
      if (
        next.item &&
        options?.visibleItemIds &&
        !options.visibleItemIds.includes(next.item)
      ) {
        return { ...next, item: null }
      }
      return next
    },
    []
  )

  // Every filter bar change deselects the item (and any look-back opened
  // from it), even when it would still be in the new list, so the user
  // picks a new item after changing what the list shows. A patch that
  // changes no filter is not a change: it keeps the item and the page.
  const setFilter = useCallback(
    (patch: InboxFilterPatch) => {
      const current = currentParams()
      if (
        isNoopFilterPatch(current, patch, setupState, writtenFeedbackEnabled)
      ) {
        return
      }
      replaceParams({
        ...applyFilterPatch(current, patch, setupState, writtenFeedbackEnabled),
        item: null,
        lookback: null,
      })
    },
    [currentParams, replaceParams, setupState, writtenFeedbackEnabled]
  )

  const setPage = useCallback(
    (page: number, perPage?: PageSize) => {
      const current = currentParams()
      replaceParams({ ...current, page, perPage: perPage ?? current.perPage })
    },
    [currentParams, replaceParams]
  )

  const setSearch = useCallback(
    (q: string, options?: SetSearchOptions) => {
      const current = currentParams()
      const next = applyKeepItemRule({ ...current, q, page: 1 }, options)
      replaceParams(next)
    },
    [applyKeepItemRule, currentParams, replaceParams]
  )

  const selectItem = useCallback(
    (id: string | null) => {
      pushParams({ ...currentParams(), item: id })
    },
    [currentParams, pushParams]
  )

  const openLookback = useCallback(
    (id: string) => {
      pushParams({ ...currentParams(), lookback: id })
    },
    [currentParams, pushParams]
  )

  const closeLookback = useCallback(() => {
    pushParams({ ...currentParams(), lookback: null })
  }, [currentParams, pushParams])

  return {
    params,
    setFilter,
    setPage,
    setSearch,
    selectItem,
    openLookback,
    closeLookback,
  }
}
